import React, { useMemo, useState, useEffect } from "react";
import styles from "./QuestProgressCard.module.scss";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import type { QuestWithProgress } from "@/hooks/useUserQuestStats";
import { useGetQuestConfigsForUser } from "@/hooks/useGetQuestConfigsForUser";
import { useGetAllQuestConfigsForUser } from "@/hooks/useGetAllQuestConfigsForUser";
import { useGetQuestProgressionForUser } from "@/hooks/useGetQuestProgressionForUser";
import { useQueryClient } from "@tanstack/react-query";
import Skeleton from "@/components/skeletons/Skeleton";
import { FaTrophy } from "react-icons/fa";
import { IoGameControllerOutline } from "react-icons/io5";
import getConfig from "@/utils/getConfig";
import Toast from "@/components/Toast";
import { OverwolfApi, OWGameResponse, QuestConfig, QuestProgression, WebsocketEvent } from "@devour/client";
import useOverwolfInterop from "@/hooks/useOverwolfInterop";
import useWebsocket from "@/hooks/useWebsocket";
import { useAllGamesList } from "@/hooks/useAllGamesQuests";

export type { QuestWithProgress } from "@/hooks/useUserQuestStats";

type FilterType = "all" | "claimable" | "in-progress" | "claimed";
type TimePeriodFilter = "all" | "daily" | "weekly" | "monthly" | "all-time";

// New Quest System Data Types
interface QuestWithNewProgress {
    questConfig: QuestConfig;
    progression?: QuestProgression;
    gameInfo?: OWGameResponse;
    // Computed properties for UI
    totalProgress: number;
    totalTarget: number;
    percentComplete: number;
    isClaimable: boolean;
    isClaimed: boolean;
    resetType: "daily" | "weekly" | "monthly" | "never";
}

interface QuestProgressCardProps {
    onQuestsLoaded?: (quests: QuestWithProgress[]) => void;
    compact?: boolean; // Force compact mode
}

export const QuestProgressCard: React.FC<QuestProgressCardProps> = ({ onQuestsLoaded, compact = false }) => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const isOnOverwolf = useSelector((store: IStore) => store.metaStore.isOnOverwolf);
    const queryClient = useQueryClient();
    const [claimingReward, setClaimingReward] = useState<string | null>(null);
    const [filter, setFilter] = useState<FilterType>("in-progress");
    const [timePeriodFilter, setTimePeriodFilter] = useState<TimePeriodFilter>("all");
    const [gameFilter, setGameFilter] = useState<string>("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10); // Fixed page size for client-side pagination
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [showToast, setShowToast] = useState(false);
    const [expandedQuests, setExpandedQuests] = useState<Set<string>>(new Set());
    const [isMobile, setIsMobile] = useState(false);

    // Detect if we're on mobile
    React.useEffect(() => {
        const checkMobile = () => {
            const actualIsMobile = window.innerWidth <= 768;
            setIsMobile(actualIsMobile);
        };
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    // NEW QUEST SYSTEM: Fetch quest configs and progressions following reference pattern
    const shouldFetchQuestConfigs = Boolean(fullToken && gameFilter); // Fetch for any specific game OR for "all" games

    /*
     * For "all games" mode, use the new all quest configs API
     * For specific games, use the existing game-specific API
     */
    const isAllGamesMode = gameFilter === "all";

    // Use new "all games" API when gameFilter is "all"
    const allGamesQuery = isAllGamesMode && shouldFetchQuestConfigs ? {} : undefined;

    const { data: allQuestConfigs, isLoading: isLoadingAllQuestConfigs, refetch: refetchAllQuestConfigs } = useGetAllQuestConfigsForUser(
        fullToken,
        allGamesQuery,
    );

    /*
     * Use game-specific API when a specific game is selected (not "all")
     * This API supports both "all games" and specific game filtering
     */
    const gameSpecificQuery = !isAllGamesMode && shouldFetchQuestConfigs ? { gameId: gameFilter } : undefined;

    const { data: gameQuestConfigs, isLoading: isLoadingGameQuestConfigs, refetch: refetchGameQuestConfigs } = useGetQuestConfigsForUser(
        fullToken,
        gameSpecificQuery || { gameId: null }, // Provide default to prevent hook from being called conditionally
    );

    // Determine which quest configs to use based on mode
    const questConfigs = isAllGamesMode ? allQuestConfigs : gameQuestConfigs;
    const isLoadingQuestConfigs = isAllGamesMode ? isLoadingAllQuestConfigs : isLoadingGameQuestConfigs;

    // Only get quest IDs if we have valid quest configs
    const questConfigIds = questConfigs?.map(q => q.id).filter(Boolean) || [];


    const { data: questProgressions, isLoading: isLoadingQuestProgressions, refetch: refetchQuestProgressions } = useGetQuestProgressionForUser(
        fullToken,
        questConfigIds,
    );

    // Keep game list for dropdown (legacy endpoint still works for this)
    const { data: gamesList, isLoading: loadingGames } = useAllGamesList();

    // Determine authentication and loading states
    const isAuthenticated = !!fullToken;
    const isLoading = isLoadingQuestConfigs || isLoadingQuestProgressions;

    // Create unified refetch function
    const refetch = () => {
        if (isAllGamesMode) {
            void refetchAllQuestConfigs();
        } else {
            void refetchGameQuestConfigs();
        }
        /*
         * Only refetch quest progressions if we have valid questConfigIds
         * Manual refetch calls bypass the enabled condition, so we need to check manually
         */
        if (questConfigIds && questConfigIds.length > 0) {
            void refetchQuestProgressions();
        }
    };

    // WebSocket support for real-time updates following reference pattern
    useWebsocket(WebsocketEvent.QUESTPROGRESSED, () => {
        console.log("invalidating quest progression");
        queryClient.invalidateQueries({ queryKey: ["quest-progression-user"] });
    });

    /*
     * Helper function to get game name for a quest
     */
    const getGameNameForQuest = (quest: QuestConfig): string => {
        // Try to get game name from quest's game relationship
        if (quest.game?.name) return quest.game.name;
        if (quest.game?.title) return quest.game.title;

        // Fallback: try to derive from quest name or description
        const questName = quest.name?.toLowerCase() || "";
        if (questName.includes("rocket league")) return "Rocket League";
        if (questName.includes("fortnite")) return "Fortnite";
        if (questName.includes("marvel rivals")) return "Marvel Rivals";
        if (questName.includes("cs2")) return "CS2";
        if (questName.includes("dqfe2e4")) return "DQFE2E4";

        // Final fallback
        return quest.name || "Unknown Game";
    };

    // Helper function to toggle quest expansion
    const toggleQuestExpansion = (questId: string) => {
        setExpandedQuests(prev => {
            const newSet = new Set(prev);
            if (newSet.has(questId)) {
                newSet.delete(questId);
            } else {
                newSet.add(questId);
            }
            return newSet;
        });
    };

    // Process quest data following reference implementation pattern
    const quests = useMemo(() => {
        if (!questConfigs || !questProgressions) return [];

        return questConfigs.map((questConfig) => {
            const progression = questProgressions.find((prog) => prog?.questConfigId === questConfig?.id);

            // Calculate progress from milestones
            let totalProgress = 0;
            let totalTarget = 0;
            let hasClaimableMilestone = false;
            const isClaimed = progression?.claimedAt ?? false;
            const isComplete = progression?.isComplete ?? false;

            if (questConfig.milestones && questConfig.milestones.length > 0) {
                questConfig.milestones.forEach(milestone => {
                    const milestoneProgression = progression?.progression?.[milestone.id];
                    if (milestoneProgression) {
                        totalProgress += milestoneProgression.amount || 0;
                        totalTarget += milestone.targetAmount || 0;
                        if (!milestoneProgression.isComplete && (milestoneProgression.amount || 0) >= (milestone.targetAmount || 0)) {
                            hasClaimableMilestone = true;
                        }
                    } else {
                        totalTarget += milestone.targetAmount || 0;
                    }
                });
            }

            const percentComplete = totalTarget > 0 ? Math.min(totalProgress / totalTarget * 100, 100) : 0;
            const isClaimable = hasClaimableMilestone || isComplete && !isClaimed;

            // Map quest reset type
            const getResetType = (questReset: string): "daily" | "weekly" | "monthly" | "never" => {
                switch (questReset?.toUpperCase()) {
                    case "DAILY": return "daily";
                    case "WEEKLY": return "weekly";
                    case "MONTHLY": return "monthly";
                    default: return "never";
                }
            };


            /*
             * Find game info for display
             * For specific game mode, find the game from gamesList based on gameFilter
             * For "all games" mode, we need to determine the game from the quest config's game context
             * Since quest configs in "all games" mode don't necessarily include game info directly,
             * we'll use "All Games" as fallback and let the UI show quest names
             */
            const gameInfo = isAllGamesMode
                ? null // In all games mode, we show individual game names per quest
                : gamesList?.find(g => g.gameId?.toString() === gameFilter);

            // Return object compatible with existing filter logic
            return {
                quest: {
                    _id: questConfig.id,
                    id: questConfig.id,
                    name: questConfig.name,
                    description: questConfig.description,
                    target: totalTarget,
                    resetType: questConfig.questReset,
                    milestones: questConfig.milestones,
                    requireProgressionOrder: questConfig.requireProgressionOrder,
                },
                game: gameInfo,
                gameId: gameInfo?.gameId || gameInfo?.id,
                progress: totalProgress,
                target: totalTarget,
                isClaimable,
                isClaimed,
                percentComplete,
                type: getResetType(questConfig.questReset),
                xpReward: 0, // TODO: Calculate from reward configs
                fuelReward: 0, // TODO: Calculate from reward configs
                rewardId: questConfig.id,
                canClaim: isClaimable,
                isExpired: false,
                hasRecentClaims: isClaimed,
                claimedMilestoneIndexes: [],
                hasClaimableMilestone,
                claimableMilestone: null,
                resetType: questConfig.questReset,
                otherReward: null,
                // New system specific data
                questConfig,
                progression,
            };
        });
    }, [questConfigs, questProgressions, gamesList, gameFilter]);

    // Calculate summary statistics following the expected format
    const summary = useMemo(() => {
        const claimableQuests = quests.filter(q => q.isClaimable && !q.isClaimed);
        const inProgressQuests = quests.filter(q => !q.isClaimable && !q.isClaimed);
        const claimedQuests = quests.filter(q => q.isClaimed);

        return {
            claimableCount: claimableQuests.length,
            inProgressCount: inProgressQuests.length,
            claimedCount: claimedQuests.length,
            totalCount: quests.length,
        };
    }, [quests]);

    /*
     * Listen for Overwolf game events and refetch quest data immediately
     * This ensures real-time updates when playing games on another window/machine
     * CRITICAL: Only register Overwolf callbacks when actually on Overwolf platform
     */
    useOverwolfInterop(isOnOverwolf ? (ev) => {
        if (ev.type === "ow:game-events-logged") {
            void refetch();
            // Also invalidate the cache to ensure fresh data
            void queryClient.invalidateQueries(["quest-progression-user"]);
            void queryClient.invalidateQueries(["quest-configs-user"]);
            void queryClient.invalidateQueries(["quest-configs-user-all"]);
        }
    } : undefined);


    // Game filter changes are automatically handled by the useGetQuestConfigsForUser hook

    // Force refetch when page becomes visible (laptop wake, tab switch back)
    React.useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                void refetch();
                // Invalidate cache to force fresh data
                void queryClient.invalidateQueries(["quest-progression-user"]);
                void queryClient.invalidateQueries(["quest-configs-user"]);
                void queryClient.invalidateQueries(["quest-configs-user-all"]);
            }
        };

        const handleFocus = () => {
            void refetch();
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", handleFocus);

        // Also refetch immediately on mount to catch any missed updates
        void refetch();

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("focus", handleFocus);
        };
    }, [refetch, queryClient]);

    // Track the previous game filter to detect changes
    const [prevGameFilter, setPrevGameFilter] = useState<string>("all");

    // Clear data when game filter changes
    React.useEffect(() => {
        if (gameFilter !== prevGameFilter) {
            setPrevGameFilter(gameFilter);
        }
    }, [gameFilter, prevGameFilter]);

    // Generate games list for dropdown filtering
    const gamesWithQuests = useMemo(() => {
        return (gamesList || [])
            .filter(game => game._id && game.name) // Ensure we have both MongoDB ID and name
            .sort((a, b) => (a.name || "").localeCompare(b.name || "")) // Sort alphabetically
            .map(game => ({
                id: game._id || "", // Use MongoDB ObjectId for new quest system
                overwolfId: game.gameId?.toString() || "", // Keep Overwolf ID for reference
                name: game.name || "",
            }));
    }, [gamesList]);

    // Apply filtering to quests and calculate consistent counts
    const { filteredQuests, filterCounts, timePeriodCounts } = useMemo(() => {
        // Filter quests by status
        const allQuests = quests.filter(q => !q.isExpired);
        const claimableQuests = quests.filter(q => q.isClaimable && !q.isClaimed && !q.isExpired);
        const inProgressQuests = quests.filter(q => !q.isClaimable && !q.isClaimed && !q.isExpired);
        const claimedQuests = quests.filter(q => q.isClaimed);

        const counts = {
            all: allQuests.length,
            claimable: claimableQuests.length,
            "in-progress": inProgressQuests.length,
            claimed: claimedQuests.length,
        };

        let filtered;
        switch (filter) {
            case "claimable":
                filtered = claimableQuests;
                break;
            case "in-progress":
                filtered = inProgressQuests;
                break;
            case "claimed":
                filtered = claimedQuests;
                break;
            case "all":
            default:
                filtered = allQuests;
                break;
        }

        // Apply time period filter to ALL main filters (not just in-progress)
        if (timePeriodFilter !== "all") {
            filtered = filtered.filter(q => {
                const resetType = q.type || q.quest?.resetType || q.resetType || "never";
                if (timePeriodFilter === "daily") return resetType === "daily";
                if (timePeriodFilter === "weekly") return resetType === "weekly";
                if (timePeriodFilter === "monthly") return resetType === "monthly";
                if (timePeriodFilter === "all-time") return resetType === "never" || resetType === "none";
                return true;
            });
        }

        // Sort by Reset Type (Daily, Weekly, Monthly, None) then Game Name
        filtered = filtered.sort((a, b) => {
            // First sort by reset type
            const resetOrder = { "daily": 1, "weekly": 2, "monthly": 3, "never": 4, "none": 4 };
            const aReset = a.type || a.quest?.resetType || a.resetType || "never";
            const bReset = b.type || b.quest?.resetType || b.resetType || "never";
            const aOrder = resetOrder[aReset] || 5;
            const bOrder = resetOrder[bReset] || 5;

            if (aOrder !== bOrder) return aOrder - bOrder;

            // Then sort by game name
            const aGame = a.game?.name || "";
            const bGame = b.game?.name || "";
            return aGame.localeCompare(bGame);
        });

        // Calculate time period counts for current filter
        const currentFilterQuests = (() => {
            switch (filter) {
                case "claimable": return claimableQuests;
                case "in-progress": return inProgressQuests;
                case "claimed": return claimedQuests;
                case "all":
                default: return allQuests;
            }
        })();

        const periodCounts = {
            all: currentFilterQuests.length,
            daily: currentFilterQuests.filter(q => (q.type || q.quest?.resetType || q.resetType) === "daily").length,
            weekly: currentFilterQuests.filter(q => (q.type || q.quest?.resetType || q.resetType) === "weekly").length,
            monthly: currentFilterQuests.filter(q => (q.type || q.quest?.resetType || q.resetType) === "monthly").length,
            "all-time": currentFilterQuests.filter(q => {
                const resetType = q.type || q.quest?.resetType || q.resetType || "never";
                return resetType === "never" || resetType === "none";
            }).length,
        };

        return { filteredQuests: filtered, filterCounts: counts, timePeriodCounts: periodCounts };
    }, [quests, filter, timePeriodFilter]);

    // Client-side pagination for filtered results
    const { paginatedQuests, pagination: clientPagination } = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedData = filteredQuests.slice(startIndex, endIndex);

        const totalPages = Math.ceil(filteredQuests.length / pageSize);
        const hasMore = currentPage < totalPages;

        return {
            paginatedQuests: paginatedData,
            pagination: {
                total: filteredQuests.length,
                page: currentPage,
                totalPages,
                limit: pageSize,
                hasMore,
                offset: startIndex,
            },
        };
    }, [filteredQuests, currentPage, pageSize]);

    // Reset to page 1 when filter changes
    React.useEffect(() => {
        setCurrentPage(1);
    }, [filter, timePeriodFilter]);

    // Helper function to render progress status (reduces nested ternary complexity)
    const renderProgressStatus = (questData: QuestWithProgress) => {
        if (questData.isExpired && questData.isClaimed) {
            return <span className={styles.claimed}>Claimed (Historical)</span>;
        }
        if (questData.isClaimed) {
            return <span className={styles.claimed}>Claimed</span>;
        }
        if (questData.isExpired) {
            return <span className={styles.expired}>Expired</span>;
        }
        if (questData.isClaimable && !questData.hasClaimableMilestone) {
            return <span className={styles.complete}>Complete!</span>;
        }
        return (
            <>
                <div className={styles.progressBar}>
                    <div
                        className={styles.progressFill}
                        style={{ width: `${questData.percentComplete}%` }}
                    />
                </div>
                <span className={styles.progressText}>
                    {questData.progress}/{questData.target}
                </span>
            </>
        );
    };

    // Helper function to render action buttons (reduces nested ternary complexity)
    const renderActionButton = (questData: QuestWithProgress) => {
        const claimId = `${questData.gameId || questData.game?.id || "global"}-${questData.rewardId || questData.quest?._id || questData.quest?.id}`;
        const isClaimingThis = claimingReward === claimId;

        if (questData.isClaimed) {
            return <span className={styles.claimed}>Claimed</span>;
        }
        if (questData.hasClaimableMilestone) {
            return (
                <button
                    className={styles.claimButton}
                    onClick={() => handleClaimReward(questData)}
                    disabled={isClaimingThis}
                >
                    {isClaimingThis ? "Claiming..." : "Claim Milestone"}
                </button>
            );
        }
        if (questData.isClaimable) {
            return (
                <button
                    className={styles.claimButton}
                    onClick={() => handleClaimReward(questData)}
                    disabled={isClaimingThis}
                >
                    {isClaimingThis ? "Claiming..." : "Claim"}
                </button>
            );
        }
        if (questData.percentComplete >= 80) {
            return <span className={styles.nearComplete}>Almost there!</span>;
        }
        return <span className={styles.inProgress}>Active</span>;
    };

    // Helper function to render desktop action buttons (different from mobile)
    const renderDesktopActionButton = (questData: QuestWithProgress) => {
        const claimId = `${questData.gameId || questData.game?.id || "global"}-${questData.rewardId || questData.quest?._id || questData.quest?.id}`;
        const isClaimingThis = claimingReward === claimId;

        if (questData.isClaimed) {
            return <span className={styles.claimed}>-</span>;
        }
        if (questData.hasClaimableMilestone) {
            return (
                <button
                    className={styles.claimButton}
                    onClick={() => handleClaimReward(questData)}
                    disabled={isClaimingThis}
                >
                    {isClaimingThis ? "Claiming..." : "Claim Milestone"}
                </button>
            );
        }
        if (questData.isClaimable) {
            return (
                <button
                    className={styles.claimButton}
                    onClick={() => handleClaimReward(questData)}
                    disabled={isClaimingThis}
                >
                    {isClaimingThis ? "Claiming..." : "Claim"}
                </button>
            );
        }
        if (questData.percentComplete >= 80) {
            return <span className={styles.nearComplete}>Almost!</span>;
        }
        return <span className={styles.inProgress}>Active</span>;
    };

    // Helper function to generate button className and avoid nested ternaries
    const getButtonClassName = (baseClass: string, isActive: boolean) => {
        return `${styles[baseClass]} ${isActive ? styles.active : ""}`;
    };

    // Helper function to generate reset period display text
    const getResetPeriodText = (type: string, isCard = false) => {
        if (type === "never") {
            return isCard ? "No Reset" : "None";
        }
        return type.charAt(0).toUpperCase() + type.slice(1);
    };

    // Notify parent component if callback provided (for backwards compatibility)
    React.useEffect(() => {
        if (onQuestsLoaded) {
            onQuestsLoaded(quests);
        }
    }, [quests, onQuestsLoaded]);

    const handleClaimReward = async (quest: QuestWithProgress) => {
        if (!fullToken || claimingReward) return;

        const rewardId = quest.rewardId || quest.quest?._id || quest.quest?.id;
        const gameId = quest.gameId || quest.game?.id;

        if (!rewardId) {
            // Cannot claim reward: missing rewardId
            return;
        }

        // Use a unique identifier combining gameId and rewardId to handle duplicate rewards across games
        const uniqueClaimId = `${gameId || "global"}-${rewardId}`;
        setClaimingReward(uniqueClaimId);

        // For milestone quests, get the rewardConfigId
        let rewardConfigId: string | undefined;
        if (quest.hasClaimableMilestone && quest.claimableMilestone) {
            /*
             * First try the original rewardConfigId field (if it exists)
             * Then fall back to the populated rewardConfig.id (like GoVip uses)
             */
            rewardConfigId = quest.claimableMilestone.rewardConfigId ||
                       quest.claimableMilestone.rewardConfig?.id ||
                       quest.claimableMilestone.rewardConfig?._id;
        }

        try {

            // Use the typed API client like GoVip does
            const data = await new OverwolfApi(getConfig(fullToken)).claimOverwolfReward({
                rewardId,
                gameId: gameId || undefined,
                rewardConfigId,
            });


            // Show success toast with reward info
            const rewards = data.rewards || [];
            let message = "Reward claimed successfully!";
            if (rewards.length > 0) {
                const rewardTexts = rewards.map((r: any) => {
                    // Use the name if available, otherwise use amount + type
                    if (r.name) {
                        return r.amount && r.amount > 1 ? `${r.amount}x ${r.name}` : r.name;
                    }
                    // For XP and FUEL, show amount
                    if (r.rewardType === "XP" && r.amount) {
                        return `${r.amount} XP`;
                    }
                    if (r.rewardType === "FUEL" && r.amount) {
                        return r.amountUnit === "FIAT" ? `$${r.amount}` : `${r.amount} FUEL`;
                    }
                    // Fallback to description or type
                    return r.description || r.rewardType || "Reward";
                });

                // Format message based on number of rewards
                if (rewardTexts.length === 1) {
                    message = `${rewardTexts[0]} successfully claimed!`;
                } else if (rewardTexts.length === 2) {
                    message = `${rewardTexts.join(" and ")} successfully claimed!`;
                } else {
                    const allButLast = rewardTexts.slice(0, -1);
                    const last = rewardTexts[rewardTexts.length - 1];
                    message = `${allButLast.join(", ")}, and ${last} successfully claimed!`;
                }
            }
            setToastMessage(message);
            setShowToast(true);

            // Invalidate the query to refetch updated data
            await queryClient.invalidateQueries(["devourplay-user-quest-stats"]);

            // Small delay to allow backend to update claimLogs
            setTimeout(async () => {
                // Refetch to get the updated data
                await refetch();
            }, 1000);
        } catch (error) {
            // Error claiming reward - show error message to user
            setToastMessage("Failed to claim reward. Please try again.");
            setShowToast(true);
        } finally {
            setClaimingReward(null);
        }
    };

    if (isLoading) {
        return (
            <div className={styles.questProgressCard}>
                <div className={styles.header}>
                    <div className={styles.titleGroup}>
                        <span className={styles.iconWrapper}>
                            <FaTrophy />
                        </span>
                        <h3>Quest Progression</h3>
                    </div>
                </div>
                <Skeleton height={300} rows={5} />
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className={styles.questProgressCard}>
                <div className={styles.header}>
                    <div className={styles.titleGroup}>
                        <span className={styles.iconWrapper}>
                            <FaTrophy />
                        </span>
                        <h3>Quest Progression</h3>
                    </div>
                </div>
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>🏆</div>
                    <h4>Your Quest Dashboard Awaits!</h4>
                    <p>Sign in to see all active quests, track progress, and claim XP rewards.</p>
                </div>
            </div>
        );
    }

    /*
     * Note: Authenticated users should ALWAYS see filters and game dropdown
     * even if they have no progress yet. They can select a game to see available quests.
     */

    return (
        <div className={`${styles.questProgressCard} ${compact ? styles.compact : ""}`}>
            <div className={styles.header}>
                <div className={styles.titleGroup}>
                    <span className={styles.iconWrapper}>
                        <FaTrophy />
                    </span>
                    <h3>Quest Progression</h3>
                </div>
                {summary.claimableCount > 0 &&
          <span className={styles.claimableBadge}>
              {summary.claimableCount} Ready to Claim!
          </span>
                }
            </div>

            {/* Filter buttons */}
            <div className={styles.filterButtons}>
                <button
                    className={getButtonClassName("filterButton", filter === "in-progress")}
                    onClick={() => setFilter("in-progress")}
                >
                    Active ({filterCounts["in-progress"]})
                </button>
                <button
                    className={getButtonClassName("filterButton", filter === "claimable")}
                    onClick={() => setFilter("claimable")}
                >
                    Claimable ({filterCounts.claimable})
                </button>
                <button
                    className={getButtonClassName("filterButton", filter === "claimed")}
                    onClick={() => setFilter("claimed")}
                >
                    Claimed ({filterCounts.claimed})
                </button>
                <button
                    className={getButtonClassName("filterButton", filter === "all")}
                    onClick={() => setFilter("all")}
                >
                    All ({filterCounts.all})
                </button>

                {/* Game Filter Dropdown */}
                {gamesWithQuests.length > 0 &&
          <div className={styles.gameFilterWrapper}>
              <select
                  className={styles.gameFilterDropdown}
                  value={gameFilter}
                  onChange={(e) => setGameFilter(e.target.value)}
              >
                  <option value="all">All Games</option>
                  {gamesWithQuests.map(game =>
                      <option key={game.id} value={game.id}>{game.name}</option>)}
              </select>
          </div>
                }
            </div>

            {/* Time Period Filters - Show for ALL main filters */}
            <div className={styles.timePeriodFilters}>
                <button
                    className={getButtonClassName("timePeriodButton", timePeriodFilter === "all")}
                    onClick={() => setTimePeriodFilter("all")}
                >
                    {isMobile ? `All (${timePeriodCounts.all})` : `All (${timePeriodCounts.all})`}
                </button>
                <button
                    className={getButtonClassName("timePeriodButton", timePeriodFilter === "daily")}
                    onClick={() => setTimePeriodFilter("daily")}
                >
                    {isMobile ? `D (${timePeriodCounts.daily})` : `Daily (${timePeriodCounts.daily})`}
                </button>
                <button
                    className={getButtonClassName("timePeriodButton", timePeriodFilter === "weekly")}
                    onClick={() => setTimePeriodFilter("weekly")}
                >
                    {isMobile ? `W (${timePeriodCounts.weekly})` : `Weekly (${timePeriodCounts.weekly})`}
                </button>
                <button
                    className={getButtonClassName("timePeriodButton", timePeriodFilter === "monthly")}
                    onClick={() => setTimePeriodFilter("monthly")}
                >
                    {isMobile ? `M (${timePeriodCounts.monthly})` : `Monthly (${timePeriodCounts.monthly})`}
                </button>
                <button
                    className={getButtonClassName("timePeriodButton", timePeriodFilter === "all-time")}
                    onClick={() => setTimePeriodFilter("all-time")}
                >
                    {isMobile ? `None (${timePeriodCounts["all-time"]})` : `None (${timePeriodCounts["all-time"]})`}
                </button>
            </div>

            {/* Mobile/Compact Card View */}
            {isMobile &&
                <div className={styles.questCards}>
                    {paginatedQuests.map((questData, index) =>
                        <div
                            key={`quest-${questData.rewardId || questData.quest?._id}-${questData.gameId || "global"}-${index}`}
                            className={`${styles.questCard} ${questData.isClaimable ? styles.claimable : ""}`}
                        >
                            {/* Quest Header */}
                            <div className={styles.questCardHeader}>
                                <div className={styles.questInfo}>
                                    <span className={styles.gameTag}>
                                        {questData.game?.name || questData.game?.title || getGameNameForQuest(questData.quest)}
                                    </span>
                                    <div className={styles.questNameContainer}>
                                        {questData.quest.milestones && questData.quest.milestones.length > 1 &&
                                            <button
                                                className={styles.expandButton}
                                                onClick={() => toggleQuestExpansion(questData.quest.id)}
                                                aria-label={expandedQuests.has(questData.quest.id) ? "Collapse milestones" : "Expand milestones"}
                                            >
                                                {expandedQuests.has(questData.quest.id) ? "▼" : "▶"}
                                            </button>
                                        }
                                        <h4 className={styles.questName}>
                                            {questData.quest.name}
                                        </h4>
                                    </div>
                                </div>
                                <span className={`${styles.resetPeriod} ${styles[questData.type]}`}>
                                    {getResetPeriodText(questData.type, true)}
                                </span>
                            </div>

                            {/* Quest Progress */}
                            <div className={styles.questCardProgress}>
                                {renderProgressStatus(questData)}
                            </div>

                            {/* Milestone Details (Accordion) */}
                            {expandedQuests.has(questData.quest.id) && questData.quest.milestones && questData.quest.milestones.length > 1 &&
                                <div className={styles.milestonesContainer}>
                                    <div className={styles.milestonesHeader}>
                                        <span className={styles.milestonesTitle}>
                                            Milestones ({questData.quest.requireProgressionOrder ? "Sequential" : "Parallel"})
                                        </span>
                                        <span className={styles.milestonesSubtitle}>
                                            {questData.quest.requireProgressionOrder
                                                ? "Complete in order"
                                                : "Complete in any order"
                                            }
                                        </span>
                                    </div>
                                    <div className={styles.milestonesList}>
                                        {questData.quest.milestones.map((milestone, index) => {
                                            const milestoneProgression = questData.progression?.progression?.[milestone.id];
                                            const progress = milestoneProgression?.amount || 0;
                                            const target = milestone.targetAmount || 0;
                                            const isComplete = milestoneProgression?.isComplete || progress >= target;
                                            const percentage = target > 0 ? Math.min(progress / target * 100, 100) : 0;
                                            const isLocked = questData.quest.requireProgressionOrder && index > 0 &&
                                                !questData.quest.milestones.slice(0, index).every(prevMilestone => {
                                                    const prevProgression = questData.progression?.progression?.[prevMilestone.id];
                                                    return prevProgression?.isComplete || (prevProgression?.amount || 0) >= (prevMilestone.targetAmount || 0);
                                                });

                                            return (
                                                <div key={milestone.id}>
                                                    <div
                                                        className={`${styles.milestoneItem} ${isComplete ? styles.complete : ""} ${isLocked ? styles.locked : ""}`}
                                                    >
                                                        <div className={styles.milestoneInfo}>
                                                            <span className={styles.milestoneNumber}>
                                                                {questData.quest.requireProgressionOrder ? `${index + 1}.` : "•"}
                                                            </span>
                                                            <span className={styles.milestoneName}>
                                                                {milestone.name || `Milestone ${index + 1}`}
                                                            </span>
                                                            {isLocked && <span className={styles.lockIcon}>🔒</span>}
                                                        </div>
                                                        <span className={styles.milestoneProgress}>
                                                            {progress}/{target}
                                                        </span>
                                                        <div className={styles.milestoneProgressBar}>
                                                            <div
                                                                className={styles.milestoneProgressFill}
                                                                style={{ width: `${percentage}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                    {milestone.description &&
                                                        <div className={styles.milestoneDescription}>
                                                            {milestone.description}
                                                        </div>
                                                    }
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            }

                            {/* Quest Footer with Rewards and Action */}
                            <div className={styles.questCardFooter}>
                                <div className={styles.rewards}>
                                    {questData.xpReward > 0 &&
                    <span className={styles.xpReward}>{questData.xpReward} XP</span>
                                    }
                                    {questData.fuelReward > 0 &&
                    <span className={styles.fuelReward}>{questData.fuelReward} FUEL</span>
                                    }
                                    {questData.otherReward &&
                    <span className={styles.otherReward}>
                        {questData.otherReward.amount && questData.otherReward.amount > 1
                            ? `${questData.otherReward.amount}x `
                            : ""}
                        {questData.otherReward.name}
                    </span>
                                    }
                                    {questData.xpReward <= 0 && questData.fuelReward <= 0 && !questData.otherReward &&
                    <span className={styles.noReward}>No rewards</span>
                                    }
                                </div>
                            </div>
                        </div>)}
                </div>
            }

            {/* Desktop Table View */}
            {!isMobile &&
                <table className={styles.questTable}>
                    <thead>
                        <tr>
                            <th>Quest</th>
                            <th>Reset</th>
                            <th>Progress</th>
                            <th>Reward</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedQuests.map((questData, index) =>
                            <React.Fragment key={`quest-${questData.rewardId || questData.quest?._id}-${questData.gameId || "global"}-${index}`}>
                                <tr
                                    className={questData.isClaimable ? styles.claimable : ""}
                                >
                                    <td>
                                        <div className={styles.questCell}>
                                            <span className={styles.gameTag}>
                                                {questData.game?.name || questData.game?.title || getGameNameForQuest(questData.quest)}
                                            </span>
                                            <div className={styles.questNameContainer}>
                                                {questData.quest.milestones && questData.quest.milestones.length > 1 &&
                                                <button
                                                    className={styles.expandButton}
                                                    onClick={() => toggleQuestExpansion(questData.quest.id)}
                                                    aria-label={expandedQuests.has(questData.quest.id) ? "Collapse milestones" : "Expand milestones"}
                                                >
                                                    {expandedQuests.has(questData.quest.id) ? "▼" : "▶"}
                                                </button>
                                                }
                                                <span className={styles.questName}>
                                                    {questData.quest.name}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`${styles.resetPeriod} ${styles[questData.type]}`}>
                                            {getResetPeriodText(questData.type, false)}
                                        </span>
                                    </td>
                                    <td>
                                        <div className={styles.progressCell}>
                                            {renderProgressStatus(questData)}
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.rewards}>
                                            {questData.xpReward > 0 &&
                      <span className={styles.xpReward}>{questData.xpReward} XP</span>
                                            }
                                            {questData.fuelReward > 0 &&
                      <span className={styles.fuelReward}>{questData.fuelReward} FUEL</span>
                                            }
                                            {questData.otherReward &&
                      <span className={styles.otherReward}>
                          {questData.otherReward.amount && questData.otherReward.amount > 1
                              ? `${questData.otherReward.amount}x `
                              : ""}
                          {questData.otherReward.name}
                      </span>
                                            }
                                            {questData.xpReward <= 0 && questData.fuelReward <= 0 && !questData.otherReward &&
                      <span className={styles.noReward}>-</span>
                                            }
                                        </div>
                                    </td>
                                </tr>
                                {/* Expanded Milestones Row */}
                                {expandedQuests.has(questData.quest.id) && questData.quest.milestones && questData.quest.milestones.length > 1 &&
                                <tr className={styles.expandedRow}>
                                    <td colSpan={5} className={styles.expandedCell}>
                                        <div className={styles.milestonesContainer}>
                                            <div className={styles.milestonesHeader}>
                                                <span className={styles.milestonesTitle}>
                                                    Milestones ({questData.quest.requireProgressionOrder ? "Sequential" : "Parallel"})
                                                </span>
                                                <span className={styles.milestonesSubtitle}>
                                                    {questData.quest.requireProgressionOrder
                                                        ? "Complete in order"
                                                        : "Complete in any order"
                                                    }
                                                </span>
                                            </div>
                                            <div className={styles.milestonesList}>
                                                {questData.quest.milestones.map((milestone, index) => {
                                                    const milestoneProgression = questData.progression?.progression?.[milestone.id];
                                                    const progress = milestoneProgression?.amount || 0;
                                                    const target = milestone.targetAmount || 0;
                                                    const isComplete = milestoneProgression?.isComplete || progress >= target;
                                                    const percentage = target > 0 ? Math.min(progress / target * 100, 100) : 0;
                                                    const isLocked = questData.quest.requireProgressionOrder && index > 0 &&
                                                        !questData.quest.milestones.slice(0, index).every(prevMilestone => {
                                                            const prevProgression = questData.progression?.progression?.[prevMilestone.id];
                                                            return prevProgression?.isComplete || (prevProgression?.amount || 0) >= (prevMilestone.targetAmount || 0);
                                                        });

                                                    return (
                                                        <div key={milestone.id}>
                                                            <div
                                                                className={`${styles.milestoneItem} ${isComplete ? styles.complete : ""} ${isLocked ? styles.locked : ""}`}
                                                            >
                                                                <div className={styles.milestoneInfo}>
                                                                    <span className={styles.milestoneNumber}>
                                                                        {questData.quest.requireProgressionOrder ? `${index + 1}.` : "•"}
                                                                    </span>
                                                                    <span className={styles.milestoneName}>
                                                                        {milestone.name || `Milestone ${index + 1}`}
                                                                    </span>
                                                                    {isLocked && <span className={styles.lockIcon}>🔒</span>}
                                                                </div>
                                                                <span className={styles.milestoneProgress}>
                                                                    {progress}/{target}
                                                                </span>
                                                                <div className={styles.milestoneProgressBar}>
                                                                    <div
                                                                        className={styles.milestoneProgressFill}
                                                                        style={{ width: `${percentage}%` }}
                                                                    />
                                                                </div>
                                                            </div>
                                                            {milestone.description &&
                                                                <div className={styles.milestoneDescription}>
                                                                    {milestone.description}
                                                                </div>
                                                            }
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                }
                            </React.Fragment>)}
                    </tbody>
                </table>
            }

            {filteredQuests.length === 0 &&
        <div className={styles.noResults}>
            <span>No {filter !== "all" ? filter : ""} quests found</span>
        </div>
            }

            {/* Client-side pagination controls */}
            {clientPagination.totalPages > 1 &&
        <div className={styles.pagination}>
            <button
                className={styles.pageButton}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
            >
                Previous
            </button>
            <span className={styles.pageInfo}>
                Page {clientPagination.page} of {clientPagination.totalPages} ({clientPagination.total} total quests)
            </span>
            <button
                className={styles.pageButton}
                onClick={() => setCurrentPage(p => Math.min(clientPagination.totalPages, p + 1))}
                disabled={currentPage === clientPagination.totalPages}
            >
                Next
            </button>
        </div>
            }

            {/* Toast notification */}
            <Toast
                isOpen={showToast}
                onDismiss={() => setShowToast(false)}
                message={toastMessage || ""}
                autoHide={true}
                autoHideDelay={3000}
                variant={toastMessage?.includes("Failed") ? "error" : "success"}
            />
        </div>
    );
};